/**
 * AuthGate — signed-in app-shell access.
 *
 * Pure switch on auth state. Entitlement is NOT checked here; book-content
 * access is gated separately (BookAccessGate / per-content paywall hooks).
 *
 *   auth: loading    -> <Loader/>
 *   auth: signed-out -> /v2/onboarding
 *   auth: signed-in  -> children
 *
 * This replaces AppGate for the V2 app shell. The full soft-paywall plan
 * lives in .lovable/plan.md.
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { V2GateSkeleton } from '@/components/v2/V2Skeletons';

interface AuthGateProps {
  children: React.ReactNode;
}

export function AuthGate({ children }: AuthGateProps) {
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();

  const [showLoader, setShowLoader] = useState(authLoading);

  useEffect(() => {
    if (authLoading) {
      setShowLoader(true);
      return;
    }
    const t = setTimeout(() => setShowLoader(false), 250);
    return () => clearTimeout(t);
  }, [authLoading]);

  if (!authLoading && !user) {
    return <Navigate to="/onboarding" replace state={{ from: location.pathname }} />;
  }

  const ready = !authLoading && !!user;

  return (
    <>
      {ready && children}
      {showLoader && (
        <div
          aria-hidden={!authLoading}
          className="pointer-events-none fixed inset-0 z-[9999] transition-opacity duration-300"
          style={{ opacity: authLoading ? 1 : 0 }}
        >
          <V2GateSkeleton />
        </div>
      )}
    </>
  );
}
